import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import QRGenerator from '../components/QRGenerator';
import { Button } from '../components/ui/button';
import { QrCode, CreditCard, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const QRGeneratorPage = () => {
  return (
    <Layout>
      <SEO 
        title="Free QR Code Generator - Create QR Codes Instantly | Piper Card"
        description="Generate free QR codes for URLs, WiFi, contacts, social media, WhatsApp, and more. No signup required, no watermarks. Create unlimited QR codes instantly."
        keywords="qr code generator, free qr code, wifi qr code, whatsapp qr, vcard qr, location qr, social media qr"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-block mb-4 px-4 py-2 bg-amber-100 rounded-full">
                <span className="text-amber-700 font-semibold text-sm flex items-center gap-2">
                  <QrCode className="w-4 h-4" />
                  100% Free Tool
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Free QR Code Generator
              </h1>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Create QR codes for URLs, WiFi, contacts, social media, WhatsApp, and more. 
                No signup required, no watermarks, unlimited downloads.
              </p>
            </div>
            
            {/* QR Generator Component */}
            <QRGenerator />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-gray-900 to-gray-800">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CreditCard className="w-8 h-8 text-amber-500" />
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Need a Digital Business Card?
              </h2>
            </div>
            <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
              Create a professional digital business card with QR code, bio page, and contact sharing. 
              Share your card with anyone, anywhere.
            </p>
            <Link to="/create">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                Create Your Business Card
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Supported QR Code Types
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[
                { name: 'Website URL', desc: 'Link to any webpage' },
                { name: 'Plain Text', desc: 'Any text message' },
                { name: 'Email', desc: 'Pre-filled email' },
                { name: 'Phone Call', desc: 'Direct dial number' },
                { name: 'SMS', desc: 'Pre-filled text message' },
                { name: 'WhatsApp', desc: 'Start a chat' },
                { name: 'WiFi', desc: 'Auto-connect to network' },
                { name: 'vCard', desc: 'Contact information' },
                { name: 'Location', desc: 'Google Maps pin' },
                { name: 'Event', desc: 'Calendar event' },
                { name: 'Twitter/X', desc: 'Profile link' },
                { name: 'Instagram', desc: 'Profile link' },
                { name: 'LinkedIn', desc: 'Profile link' },
                { name: 'Facebook', desc: 'Page link' },
                { name: 'YouTube', desc: 'Channel link' },
              ].map((item, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg text-center hover:bg-amber-50 transition-colors">
                  <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Is this QR code generator really free?</h3>
                <p className="text-gray-600 text-sm">Yes! Generate unlimited QR codes with no signup, no watermarks, and no hidden fees.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Do the QR codes expire?</h3>
                <p className="text-gray-600 text-sm">No, the QR codes you generate are permanent and will work forever. The data is embedded directly in the QR code.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Can I customize the QR code colors?</h3>
                <p className="text-gray-600 text-sm">Yes! You can customize both the QR code color and background color to match your brand.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">What's the difference between this and a Business Card QR?</h3>
                <p className="text-gray-600 text-sm">This tool generates static QR codes. Our Business Card feature creates a dynamic QR code linked to an editable profile page that you can update anytime.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default QRGeneratorPage;
